import React from 'react';
import {
    Autocomplete, Box, Button, Checkbox, Paper, Popper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { isVatRowReady } from './useMissingVatReportRows';

const VatRowStyledPopper = styled(Popper)(({ theme }) => ({
    zIndex: 9999,
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#FFF',
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.15)',
        minWidth: '200px',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
        maxHeight: '280px',
    },
    '& .MuiAutocomplete-option': {
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '0.875rem',
        margin: '2px 0',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.12) !important',
            fontWeight: 600,
        },
        '&.Mui-focused': {
            background: 'rgba(249, 170, 42, 0.15) !important',
        },
        '&:hover': {
            background: 'rgba(249, 170, 42, 0.25) !important',
        },
    },
}));

const defaultFormatAmount = (amount) => {
    if (amount == null || amount === '') return '';
    const num = Number(amount);
    if (Number.isNaN(num)) return '';
    return new Intl.NumberFormat('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

const defaultFormatMonth = (month) => {
    if (!month) return '—';
    const d = new Date(month);
    if (Number.isNaN(d.getTime())) return String(month);
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

/**
 * VAT report rows that have no matching line in the imported journal source (MDB / Ramplus XLS).
 * Each row can be completed with a sort code, debit and credit account and added as a journal entry.
 */
const MissingVatReportRowsTable = ({
    rows = [],
    selectedIds = [],
    onSelectedChange,
    adding = false,
    onUpdateRow,
    onAddRows,
    sortCodeOptions = [],
    accountCardOptions = [],
    t,
    title,
    hint,
    emptyMessage,
    formatAmount = defaultFormatAmount,
    formatMonth = defaultFormatMonth,
}) => {
    const accountCardSelectOptions = (accountCardOptions || [])
        .filter(o => o && o.value1 !== 'AddItem' && o.cardId);
    const pendingRows = rows.filter(r => r.status !== 'added');

    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="success.main">
                {emptyMessage || t('NoMissingVatReportRows') || 'כל שורות דוח המע״מ נמצאו בקובץ'}
            </Typography>
        );
    }

    const renderAccountCell = (row, field, placeholder) => (
        <Autocomplete
            size="small"
            freeSolo
            options={accountCardSelectOptions}
            getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option ? `${option.cardId} - ${option.name ?? ''}` : '';
            }}
            value={
                accountCardSelectOptions.find(o => String(o.cardId) === String(row[field]))
                || row[field]
                || null
            }
            onChange={(e, newValue) => {
                const val = typeof newValue === 'string' ? newValue : (newValue?.cardId || '');
                onUpdateRow(row.id, field, val);
            }}
            onInputChange={(e, newInput, reason) => {
                if (reason === 'input') {
                    const cardIdMatch = String(newInput).split(' - ')[0]?.trim();
                    onUpdateRow(row.id, field, cardIdMatch || newInput);
                }
            }}
            disabled={row.status === 'added' || row.status === 'adding'}
            isOptionEqualToValue={(a, b) => {
                const aId = typeof a === 'string' ? a : a?.cardId;
                const bId = typeof b === 'string' ? b : b?.cardId;
                return String(aId || '') === String(bId || '');
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    error={!row[field] && row.status !== 'added'}
                    placeholder={placeholder}
                />
            )}
            PopperComponent={VatRowStyledPopper}
        />
    );

    return (
        <>
            <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 700 }}>
                {title || t('MissingVatReportRows') || 'שורות מדוח מע״מ שלא קיימות בקובץ'}
                {' '}({pendingRows.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {hint || t('MissingVatReportRowsAddHint') ||
                    'מלאו קוד מיון, חובה וזכות (מולאו אוטומטית כשאפשר) והוסיפו כפקודות יומן. חודש הדיווח נלקח מחודש דוח המע״מ.'}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                <Button
                    size="small"
                    variant="contained"
                    disabled={adding || !selectedIds.some(id => isVatRowReady(rows.find(r => r.id === id)))}
                    onClick={() => onAddRows(selectedIds)}
                >
                    {adding
                        ? (t('Processing') || 'Processing')
                        : (t('AddSelectedJournalEntries') || 'הוסף נבחרים')}
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={adding || !rows.some(isVatRowReady)}
                    onClick={() => onAddRows(rows.filter(isVatRowReady).map(r => r.id))}
                >
                    {t('AddAllReadyJournalEntries') || 'הוסף את כל המוכנים'}
                </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, width: 40 }}>
                                <Checkbox
                                    size="small"
                                    checked={pendingRows.length > 0 && pendingRows.every(r => selectedIds.includes(r.id))}
                                    onChange={(e) => onSelectedChange(e.target.checked ? pendingRows.map(r => r.id) : [])}
                                />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('File') || 'קובץ'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('ReportingMonth') || 'חודש דיווח'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('TransactionNumber') || 'מספר תנועה'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('Reference') || 'אסמכתא'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('DocumentDate') || 'תאריך'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('TransactionType') || 'עסקה/תשומה'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>{t('InvoiceAmount') || 'סכום'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, minWidth: 180 }}>{t('SortCode') || 'קוד מיון'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, minWidth: 160 }}>{t('DebitAccount') || 'חובה'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, minWidth: 160 }}>{t('CreditAccount') || 'זכות'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('Status') || 'סטטוס'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('Actions') || 'פעולות'}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow
                                key={row.id}
                                sx={{
                                    opacity: row.status === 'added' ? 0.55 : 1,
                                    bgcolor: row.status === 'error' ? '#ffebee' : undefined,
                                }}
                            >
                                <TableCell sx={{ py: 0.5 }}>
                                    <Checkbox
                                        size="small"
                                        disabled={row.status === 'added' || row.status === 'adding'}
                                        checked={selectedIds.includes(row.id)}
                                        onChange={(e) => onSelectedChange(
                                            e.target.checked
                                                ? [...selectedIds, row.id]
                                                : selectedIds.filter(id => id !== row.id)
                                        )}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{row.fileName}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{formatMonth(row.reportingMonth)}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{row.transactionNumber ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{row.reference ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{row.documentDate ?? '—'}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{row.transactionType ?? '—'}</TableCell>
                                <TableCell align="right" sx={{ fontSize: 11 }}>{formatAmount(row.invoiceAmount)}</TableCell>
                                <TableCell sx={{ py: 0.5, minWidth: 180 }}>
                                    <Autocomplete
                                        size="small"
                                        options={sortCodeOptions || []}
                                        getOptionLabel={(option) =>
                                            option ? `${option.sortCodeId} - ${option.sortCodeName ?? ''}` : ''
                                        }
                                        value={(sortCodeOptions || []).find(o => String(o.sortCodeId) === String(row.sortCodeId)) || null}
                                        onChange={(e, newValue) => onUpdateRow(row.id, 'sortCodeId', newValue?.sortCodeId || '')}
                                        disabled={row.status === 'added' || row.status === 'adding'}
                                        isOptionEqualToValue={(a, b) => String(a?.sortCodeId) === String(b?.sortCodeId)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                error={!row.sortCodeId && row.status !== 'added'}
                                                placeholder={t('SortCode') || 'Sort code'}
                                            />
                                        )}
                                        PopperComponent={VatRowStyledPopper}
                                    />
                                </TableCell>
                                <TableCell sx={{ py: 0.5, minWidth: 160 }}>
                                    {renderAccountCell(row, 'debitAccount', t('DebitAccount') || 'Debit')}
                                </TableCell>
                                <TableCell sx={{ py: 0.5, minWidth: 160 }}>
                                    {renderAccountCell(row, 'creditAccount', t('CreditAccount') || 'Credit')}
                                </TableCell>
                                <TableCell sx={{ fontSize: 11 }}>
                                    {row.status === 'added' && (
                                        <Typography variant="body2" color="success.main">{t('Added') || 'נוסף'}</Typography>
                                    )}
                                    {row.status === 'adding' && (
                                        <Typography variant="body2">{t('Processing') || '...'}</Typography>
                                    )}
                                    {row.status === 'error' && (
                                        <Typography variant="body2" color="error.main" title={row.error}>
                                            {t('Error') || 'שגיאה'}
                                        </Typography>
                                    )}
                                    {row.status === 'pending' && (
                                        <Typography variant="body2" color="text.secondary">
                                            {isVatRowReady(row) ? (t('Ready') || 'מוכן') : (t('Incomplete') || 'חסר')}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={!isVatRowReady(row) || adding || row.status === 'adding'}
                                        onClick={() => onAddRows([row.id])}
                                    >
                                        {t('Add') || 'הוסף'}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default MissingVatReportRowsTable;
